<template>
  <div class="new-password-form">
    <h2>Neues Passwort eingeben</h2>

    <div class="input_field">
      <label for="newPassword">Neues Passwort</label>

      <div class="password-wrapper">
        <input
          :type="showPassword ? 'text' : 'password'"
          id="newPassword"
          v-model="newPassword"
          placeholder="Neues Passwort"
          required
        />
        <i
          class="fas"
          :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"
          @click="togglePasswordVisibility"
        ></i>
      </div>
    </div>

    <div class="footer-wrapper">
      <div class="actions">
        <a @click.prevent="onBack">Zurück</a>
        <button @click="onSubmit">
          <div v-if="loading" class="progress-icon"></div>
          <span v-else>Absenden</span>
        </button>
      </div>
      <p class="error" v-if="errorMessage && errorMessage.length">
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { Auth } from 'aws-amplify';
import { putUserLoginDetails } from '../../services/axios/data.api';

export default {
  name: 'PasswordResetModal',
  data() {
    return {
      newPassword: '',
      showPassword: false,
      errorMessage: null,
      loading: false,
      awsErrorMessages: {
        UserNotFoundException:
          'Kombination aus Benutzername und Client-ID nicht gefunden.',
        CodeMismatchException:
          'Ungültiger Bestätigungscode angegeben, bitte versuchen Sie es erneut.',
        LimitExceededException:
          'Versuchslimit überschritten. Bitte versuchen Sie es später erneut.',
        InvalidPasswordException:
          'Das Passwort muss mindestens 8 Zeichen lang sein und einen Großbuchstaben, einen Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten.',
      },
    };
  },
  props: {
    userDetail: {
      type: Object,
      required: true,
    },
  },
  methods: {
    async onSubmit() {
      if (this.loading) return;
      try {
        this.loading = true;
        this.errorMessage = null;
        const passwordRegex =
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
        if (!this.newPassword) {
          this.errorMessage = 'Das Passwort darf nicht leer sein';
          return;
        } else if (!passwordRegex.test(this.newPassword)) {
          this.errorMessage = this.awsErrorMessages.InvalidPasswordException;
          return;
        }
        await Auth.completeNewPassword(this.userDetail, this.newPassword);
        putUserLoginDetails();
        await this.$router.push({
          path: '/',
        });
      } catch (error) {
        if (error.code) {
          this.errorMessage =
            this.awsErrorMessages[error.code] || error.message;
        }
      } finally {
        this.loading = false;
      }
    },
    onBack() {
      this.$emit('onBack');
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
  },
};
</script>

<style scoped>
.new-password-form {
  position: relative;
  background-color: white;
  text-align: left;
  display: flex;
  flex-direction: column;
  max-width: 60%;
  width: 100%;
  border-radius: 6px;
  box-sizing: border-box;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
}

.new-password-form h2 {
  color: #152939;
  margin-bottom: 30px;
  font-size: 36px;
  font-weight: 500;
}

.new-password-form .input_field {
  width: 100%;
  max-width: 100%;
  margin: 0 0 22px;
  text-align: left;
}

.new-password-form .input_field label {
  margin-bottom: 8px;
  width: 100%;
  max-width: 100%;
  font-size: 14px;
  color: #000000;
  padding: 0px;
  font-weight: 600;
  line-height: 29px;
  letter-spacing: -0.32px;
  position: relative;
  top: 0;
  left: 0;
}
.new-password-form .input_field label::after {
  content: ' *';
  color: red;
}
.new-password-form .input_field input {
  width: 100%;
  max-width: 100%;
  padding: 6px 16px;
  border: 1.2px solid #bbbbbb;
  border-radius: 10px;
  font-size: 16px;
  box-sizing: border-box;
  height: 60px;
  color: #5f697a !important;
  cursor: text;
  line-height: 19px;
  letter-spacing: 0.125px;
  font-weight: 400;
}
.new-password-form .input_field input:focus {
  outline: none;
  box-shadow: none;
}

.new-password-form .input_field input:-webkit-autofill {
  box-shadow: 0 0 0 1000px white inset !important;
  -webkit-box-shadow: 0 0 0 1000px white inset !important;
  -webkit-text-fill-color: #5f697a !important;
}
.new-password-form .input_field .password-wrapper {
  position: relative;
}

.new-password-form .input_field .password-wrapper input {
  padding-right: 2.5rem;
}

.new-password-form .input_field .password-wrapper i {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #888;
}

.new-password-form .footer-wrapper {
  position: relative;
  width: 100%;
  max-width: 100%;
}
.new-password-form .footer-wrapper .actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  width: 100%;
  max-width: 100%;
}

.new-password-form .footer-wrapper .actions a {
  font-size: 14px;
  text-align: left;
  color: #828282;
  text-decoration: underline;
  cursor: pointer;
}

.new-password-form .footer-wrapper .actions button {
  min-width: 200px;
  height: 60px;
  display: inline-block;
  margin-bottom: 0;
  white-space: nowrap;
  vertical-align: middle;
  touch-action: manipulation;
  cursor: pointer;
  user-select: none;
  background-color: #000000;
  border: 1px solid #000 !important;
  transition: all 0.2s ease-in-out;
  border-radius: 8px;
}
.new-password-form .footer-wrapper .actions button span {
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: normal;
  line-height: 38px;
  transition: all 0.2s ease-in-out;
}

.new-password-form .footer-wrapper .actions button:hover {
  background-color: transparent;
}
.new-password-form .footer-wrapper .actions button:hover span {
  color: #000000;
}
.new-password-form .footer-wrapper .error {
  position: absolute;
  left: 0;
  top: 95px;
}

.new-password-form .footer-wrapper .actions button .progress-icon {
  content: ' ';
  display: block;
  width: 25px;
  height: 25px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #fff;
  border-color: #fff transparent #fff transparent;
  animation: progress-icon-animation 1.2s linear infinite;
  margin: 0 auto;
}
.new-password-form .footer-wrapper .actions button:hover .progress-icon {
  border: 2px solid #000000;
  border-color: #000000 transparent #000000 transparent;
}

@keyframes progress-icon-animation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* media query */
@media (max-width: 767px) {
  .new-password-form {
    max-width: calc(100% - 50px);
  }
  .new-password-form h2 {
    font-size: 24px;
  }
}
</style>
